import React, { FC, useEffect, useMemo, useState } from "react";
import { IDetailNftData } from "../../../common/interfaces/common.interface";
import CustomCheckbox from "../../CustomCheckbox/CustomCheckbox";
import Fees from "../../Fees/Fees";
import FormButtons from "../../FormButtons/FormButtons";
import NftVotingCard from "./NftVotingCard/NftVotingCard";
import rightArrow from "../../../assets/arrowRightWhite.svg";
import leftArrow from "../../../assets/backIcon.svg";
import Modal from "../../Modal/Modal";
import { useAnchorWallet } from "@solana/wallet-adapter-react";
import { createNotification } from "../../../utilities/notification";
import {
  MESSAGE_TYPE,
  NFT_SKELETON_AMOUNT,
} from "../../../common/constants/common.constants";
import { withCastNftVote } from "../../../solana/methods/proposals";
import { IProposalWithMetaData } from "../../../common/interfaces/proposals.interface";
import { generateSkeletonArrays } from "../../../utilities/helpers";
import SkeletonItem from "../../SkeletonItem/SkeletonItem";
import "./NftVotingModal.scss";
import { VOTE_WITH_NFT_FEE } from "../../../common/constants/fees";
import { IClubDataForProposal } from "../../../common/interfaces/clubs.interface";
import { getUsersNfts } from "../../../solana/methods/clubs";
import { clubsStore } from "../../../contexts/clubStore";

const NftVotingModal: FC<{
  closeModal: () => void;
  proposal: IProposalWithMetaData;
  votingOption: boolean;
  clubData: IClubDataForProposal;
  updateProposalAfterNftVoting: () => void;
}> = ({
  closeModal,
  proposal,
  votingOption,
  clubData,
  updateProposalAfterNftVoting,
}) => {
  const [loading, toggleLoading] = useState(true);
  const [nfts, setNfts] = useState<IDetailNftData[]>([]);
  const [selectedNftAmount, setSelectedNftAmount] = useState(0);
  const [selectedAllNfts, toggleSelectedAllNfts] = useState(false);
  const [nftsToVote, setNftsToVote] = useState<Array<IDetailNftData>>([]);
  const [isSubmitting, toggleSubmitting] = useState(false);
  const wallet = useAnchorWallet();

  const { membersNfts } = clubsStore();

  useEffect(() => {
    void getNftsByWallet();
  }, []);

  const getNftsByWallet = async () => {
    try {
      toggleLoading(true);
      if (!clubData.isDashboardProposal) {
        setNfts(membersNfts!);
      } else {
        const nfts = await getUsersNfts(wallet!.publicKey, clubData.mint);
        setNfts(nfts);
      }
    } catch (error) {
      createNotification(MESSAGE_TYPE.ERROR, "Problem loading NFTs");
    } finally {
      toggleLoading(false);
    }
  };

  const selectAllNfts = () => {
    if (loading) return;
    if (!selectedAllNfts) {
      setSelectedNftAmount(nfts.length);
      setNftsToVote(nfts);
    } else {
      setSelectedNftAmount(0);
      setNftsToVote([]);
    }
    toggleSelectedAllNfts((prevValue) => !prevValue);
  };

  const chooseNftToVote = (nft: IDetailNftData) => {
    if (!nftsToVote.includes(nft)) {
      setSelectedNftAmount((prevValue) => prevValue + 1);
      setNftsToVote((prevValue) => [...prevValue, nft]);
    } else {
      setSelectedNftAmount((prevValue) => prevValue - 1);
      const updatedNfts = nftsToVote.filter(
        (nftToVote) => nftToVote.mint !== nft.mint
      );
      setNftsToVote(updatedNfts);
    }
  };

  const voteWithNft = async () => {
    try {
      toggleSubmitting(true);
      await withCastNftVote(
        proposal,
        clubData.clubName,
        wallet!,
        clubData.treasury,
        votingOption ? 0 : 1,
        clubData.mint,
        nftsToVote,
        clubData.clubType
      );
      createNotification(MESSAGE_TYPE.SUCCESS, "Successfully voted!");
      updateProposalAfterNftVoting();
      closeModal();
    } catch (error: any) {
      console.log(error);
      createNotification(
        MESSAGE_TYPE.ERROR,
        "Problem with voting!",
        error.message
      );
    } finally {
      toggleSubmitting(false);
    }
  };

  const renderNftsForVoting = useMemo(() => {
    return !loading
      ? nfts.map((nft) => {
          return (
            <NftVotingCard
              nft={nft}
              isNftSelected={nftsToVote.includes(nft)}
              chooseNft={chooseNftToVote}
            />
          );
        })
      : generateSkeletonArrays(NFT_SKELETON_AMOUNT).map((item) => {
          return (
            <div key={item}>
              <SkeletonItem width={200} height={200} borderRadius={20} />
              <SkeletonItem width={180} height={20} borderRadius={50} />
              <SkeletonItem width={150} height={15} borderRadius={50} />
            </div>
          );
        });
  }, [loading, nfts, nftsToVote, clubData.memberRights]);

  return (
    <Modal closeModal={closeModal}>
      <div className="nft-voting-modal">
        <h3 className="nft-voting-modal__title">Choose NFT for voting</h3>
        <p className="nft-voting-modal__description">
          Select NFTs you want to use in this voting. Only selected NFTs will be
          counted towards the voting. 1 NFT = 1 vote.
        </p>
        <div className="nft-voting-modal__selections">
          <div className="nft-voting-modal__select">
            <CustomCheckbox
              checked={selectedAllNfts}
              onClickMethod={selectAllNfts}
            />
            <p>Select all</p>
          </div>
          <p className="nft-voting-modal__selected-amount">
            {selectedNftAmount} / {nfts.length} selected
          </p>
        </div>
        <div className="nft-voting-modal__nfts">{renderNftsForVoting}</div>
        <Fees
          feesTitle="Voting fee"
          feesDescription={`${nftsToVote.length * VOTE_WITH_NFT_FEE} SOL`}
        />
        <FormButtons
          cancelLabel="Go back"
          disabled={nftsToVote.length === 0}
          onCancel={closeModal}
          submitLabel="Vote"
          typeCancel="button"
          typeSubmit="button"
          submitIcon={rightArrow}
          cancelIcon={leftArrow}
          onSubmit={voteWithNft}
          submitting={isSubmitting}
        />
      </div>
    </Modal>
  );
};

export default NftVotingModal;
